import React, { useState, useEffect } from 'react';
import { X, ArrowRightCircle } from 'lucide-react';
import { api } from '../services/api';
import type { Department, User } from '../types';

interface TransferConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (showSuccessModal: boolean) => void;
  conversationId: string;
  currentDepartmentId?: string | null;
  currentAgentId?: string | null;
}

export const TransferConversationModal: React.FC<TransferConversationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  conversationId,
  currentDepartmentId,
  currentAgentId,
}) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [agents, setAgents] = useState<User[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDepartments();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedDepartmentId) {
      loadAgents(selectedDepartmentId);
    } else {
      setAgents([]);
      setSelectedAgentId('');
    }
  }, [selectedDepartmentId]);

  const loadDepartments = async () => {
    try {
      const data = await api.getDepartments();
      // Filtrar apenas departamentos ativos e excluir "Administração do Sistema"
      setDepartments(data.filter((d) =>
        d.isActive &&
        d.name !== 'Administração do Sistema'
      ));
    } catch (error) {
      console.error('Failed to load departments:', error);
      setError('Falha ao carregar departamentos');
    }
  };

  const loadAgents = async (departmentId: string) => {
    try {
      setIsLoadingAgents(true);
      const data = await api.getUsers({ departmentId, status: 'active', agentStatus: 'ONLINE' });
      // Filtrar apenas agentes não-ADMIN
      setAgents(data.filter(agent => agent.role !== 'ADMIN'));
    } catch (error) {
      console.error('Failed to load agents:', error);
      setError('Falha ao carregar agentes');
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedDepartmentId) {
      setError('Selecione um departamento');
      return;
    }

    if (
      selectedDepartmentId === currentDepartmentId &&
      selectedAgentId === (currentAgentId || '')
    ) {
      setError('Selecione um departamento ou agente diferente');
      return;
    }

    setIsLoading(true);

    try {
      await api.transferConversation(conversationId, {
        departmentId: selectedDepartmentId,
        agentId: selectedAgentId || undefined,
      });

      // Check if transfer was to a different department
      const isDifferentDepartment = selectedDepartmentId !== currentDepartmentId;

      onSuccess(isDifferentDepartment);
      onClose();
      resetForm();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Falha ao transferir conversa');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedDepartmentId('');
    setSelectedAgentId('');
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-md z-50">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <ArrowRightCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Transferir Conversa
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Departamento *
                </label>
                <select
                  value={selectedDepartmentId}
                  onChange={(e) => setSelectedDepartmentId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Selecione um departamento</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Agente (opcional)
                </label>
                {selectedDepartmentId && !isLoadingAgents && agents.length === 0 ? (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400 text-sm">
                    ⚠️ Nenhum agente online disponível neste departamento. A conversa será enviada para a fila de espera.
                  </div>
                ) : (
                  <select
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    disabled={!selectedDepartmentId || isLoadingAgents}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Fila de espera do departamento</option>
                    {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.email})
                    </option>
                  ))}
                  </select>
                )}
                {isLoadingAgents && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Carregando agentes...
                  </p>
                )}
                {!isLoadingAgents && selectedDepartmentId && agents.length > 0 && (
                  <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                    ℹ️ Apenas agentes online são exibidos na lista.
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !selectedDepartmentId}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
                >
                  <ArrowRightCircle className="w-4 h-4" />
                  <span>{isLoading ? 'Transferindo...' : 'Transferir'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
